import {
    ModalOverlay,
    ModalDialog,
    Button,
    ModalBody,
    ModalFooter,
    ModalHeader,
    DateField,
    LoadingDots
} from '@flos/react-ui';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useAddCoverageOPC } from '../../../hooks/useAddCoverageOPC';
import { jobAtom } from '../../../recoil/atoms/jobAtom';
import { CoveragePriceOPC } from './CoveragePriceOPC';
import { periodicityUnit, paymentFrequencyMap } from '../../../utils/currencyUtil';
import { useHippoText } from '../../../hooks/useHippoText';
import { convertLocalDateDTOToDate, convertLocalDateToString } from '../../../utils/dateUtil';
import usePageNavigation from '../../../hooks/usePageNavigation';

import type { AddCoveragePriceDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc';
import type { CoveragePriceOPCLabels } from './CoveragePriceOPC';
import type { HandleCloseOPCModal } from '../types';
import HighlightInfoForCoverage from './HighlightInfoForCoverage';
import { isOpcDataFilledAtom } from '../../../recoil/atoms/isOpcDataFilledAtom';
import { isOpcFetchingAtom } from '../../../recoil/atoms/isOpcFetchingAtom';
import { opcInfoAtom } from '../../../recoil/atoms/opcInfoAtom';
import { trackAddProduct } from '../../../utils/trackingUtil';
import type { CoverableOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import useOpcJob from '../../../hooks/useOpcJob';
import { hippoCoverageTextsSelector } from '../../../recoil/selectors/hippoCoverageTextsSelector';
import { getCoverableInfoSelector } from '../../../recoil/selectors/getCoverableInfoSelector';
import { businessFlowSelector } from '../../../recoil/selectors/getBusinessFlowSelector';
import { opcCoverableStatusSelector } from '../../../recoil/selectors/opcCoverableStatusSelector';
import { mockCoverableBaseDto } from '../../../__mocks__/mockCoverablesDto';
import { useQuoteJob } from '../../../hooks/useQuoteJob';
import { useAwaitQuoteJob } from '../../../hooks/useAwaitQuotedJob';
import { useState } from 'react';
import LoadingModal from '../../../shared_components/LoadingModal/LoadingModal';
import type { CustomerStopCause } from 'gw-edge-client/dist/types/dto/pc/typekey/customerstopcause.typekey';
import { QUOTED } from '../../../recoil/types/types';
import CustomerStopModal from '../../../shared_components/CustomerStopModal/CustomerStopModal';
import AddCoverageConfig, { getAddCoverageHighlightNote } from '../config/AddCoverageConfig';
import { getEffectiveDateRuleInfoSelector } from '../../../recoil/selectors/getEffectiveDateRuleSelector';
import { getOPCReceiptPageChangeTypeAtom } from '../../../recoil/atoms/getOPCReceiptPageChangeTypeAtom';

const AddCoverageModal = ({
    handleCloseModal
}: {
    handleCloseModal: HandleCloseOPCModal;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const { selectedPaymentFrequency, quoteEffectiveDate } = useRecoilValue(jobAtom);
    const { addCoveragePriceDTO, updateCoverageDate, addCoverageDTO, revertChange } =
        useAddCoverageOPC();
    const isOpcFetching = useRecoilValue(isOpcFetchingAtom);
    const isOpcDataFilled = useRecoilValue(isOpcDataFilledAtom);
    const { goBackToDetailsPage, goToShoppingBasket, goToCheckoutPage } = usePageNavigation();
    const { withdrawCurrentOPCShoppingBasketIfNotExisting } = useOpcJob();
    const { isFromDAFlow, coverageId = '', productName } = useRecoilValue(opcInfoAtom);
    const { termIds = [] } = useRecoilValue(getCoverableInfoSelector);
    const { isRuleActive, effectiveDate } = useRecoilValue(getEffectiveDateRuleInfoSelector);
    const setOPCReceiptPageChangeType = useSetRecoilState(getOPCReceiptPageChangeTypeAtom);
    const businessFlow = useRecoilValue(businessFlowSelector);
    const { quoteJob } = useQuoteJob();
    const { awaitQuoteJob } = useAwaitQuoteJob();
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [stopCause, setStopCause] = useState<CustomerStopCause>('None');
    const setJobAtom = useSetRecoilState(jobAtom);
    const [isOpen, setIsOpen] = useState(true);

    const hippoCoverageTexts = useRecoilValue(
        hippoCoverageTextsSelector({
            coverageIds: coverageId ? [coverageId] : [],
            termIds
        })
    );
    const coverageSubtitle = hippoCoverageTexts[0]?.subtitle ?? '';
    const flavourName = productName ?? '';
    const { isAddedCoverable } = useRecoilValue(opcCoverableStatusSelector);
    const isCoverageEffDateAdjustable =
        AddCoverageConfig.shouldCoverageShowEffectiveDate[coverageId] && businessFlow === 'OPC';
    const isFullScreen = false;
    const currencyPostfix = periodicityUnit[selectedPaymentFrequency ?? paymentFrequencyMap.annual];
    const getPrices = (): number[] => {
        const isMonthly = selectedPaymentFrequency === paymentFrequencyMap.monthly;

        const priceKeys: Array<
            keyof Pick<AddCoveragePriceDTO, 'coveragePrice' | 'oldPrice' | 'newPrice'>
        > = ['coveragePrice', 'oldPrice', 'newPrice'];

        return priceKeys.map((key) => {
            const totalAmount = addCoveragePriceDTO?.[key];
            return isMonthly
                ? totalAmount?.monthlyTotal?.amount ?? 0
                : totalAmount?.yearlyTotal?.amount ?? 0;
        });
    };

    const handleBackToDAShoppingBasket = async (): Promise<void> => {
        await revertChange();
        goToShoppingBasket(false);
    };

    const handleBackToDetailsPage = async (): Promise<void> => {
        await withdrawCurrentOPCShoppingBasketIfNotExisting();
        goBackToDetailsPage();
    };
    const closeCallback = isFromDAFlow ? handleBackToDAShoppingBasket : handleBackToDetailsPage;

    const [coveragePrice, oldTotalPrice, newTotalPrice] = getPrices();
    const hasHighlightNote = Boolean(
        getAddCoverageHighlightNote(coverageId, coverageSubtitle, flavourName)
    );

    const labels = {
        effectiveDateSubTitle: getHippoText('DSS-ADD-SIMPLE-COVERAGE-START-DATE-QUESTION'),
        datefieldPlaceholder: getHippoText('DSS-ADD-SIMPLE-COVERAGE-START-DATE-PLACEHOLDER'),
        cancelBtnText: getHippoText('DSS-COVERABLE-DETAILS-CANCEL'),
        submitBtnText: getHippoText('DSS-ADD-SIMPLE-COVERAGE-ADD'),
        effectiveDateRule: getHippoText('DSS-EFFECTIVE-DATE-RULE-MOTOR-TEXT', {
            date: convertLocalDateToString(convertLocalDateDTOToDate(effectiveDate ?? {})) ?? ''
        })
    };
    const shouldHideCoverageSubtitle = AddCoverageConfig.shouldHideCoverageSubtitle[productName];
    const coveragePriceOPClabels: CoveragePriceOPCLabels = {
        title: `Tilføj dækning: ${addCoveragePriceDTO?.coverageName ?? ''}`,
        coverageName: addCoveragePriceDTO?.coverageName ?? '',
        subtitle: !hasHighlightNote && !shouldHideCoverageSubtitle ? coverageSubtitle : '',
        priceChangeSubTitle: getHippoText('DSS-ADD-SIMPLE-COVERAGE-PRICE-CHANGE-HEADER', {
            flavor: addCoveragePriceDTO?.coverableName ?? ''
        }),
        coverablePriceBefore: getHippoText('DSS-ADD-SIMPLE-COVERAGE-CURRENT-PRICE'),
        coverablePriceAfter: getHippoText('DSS-ADD-SIMPLE-COVERAGE-NEW-PRICE'),
        priceChangeAlternateSubTitle: getHippoText(
            'DSS-OPC-ADD-COVERAGE-ALTERNATE-PRICE-CHANGE-SUBHEADER',
            {
                flavor: addCoveragePriceDTO?.coverableName ?? ''
            }
        )
    };
    const trackAddedCoverage: CoverableOnsaDTO = {
        ...mockCoverableBaseDto,
        flavourName: addCoveragePriceDTO?.coverableName ?? '',
        monthlyPrice: addCoveragePriceDTO?.newPrice?.monthlyTotal,
        yearlyPrice: addCoveragePriceDTO?.newPrice?.yearlyTotal,
        coverages: [
            {
                coverageId,
                coverageName: addCoveragePriceDTO?.coverageName,
                coveragePrice: {
                    monthlyPrice: addCoveragePriceDTO?.coveragePrice?.monthlyTotal,
                    yearlyPrice: addCoveragePriceDTO?.coveragePrice?.yearlyTotal
                }
            }
        ],
        publicId: '',
        termIds: []
    };

    const quoteJobInBackground = async (): Promise<void> => {
        const response = await quoteJob();
        if (response?.stopCause !== 'None') {
            setStopCause(response?.stopCause);
            setIsOpen(false);
            setShowLoadingModal(false);
        } else {
            await awaitQuoteJob();
        }
    };

    const onFinished = (): void => {
        if (stopCause === 'None') {
            setJobAtom((jobDTO) => ({ ...jobDTO, jobStatus: QUOTED }));
            handleCloseModal(goToCheckoutPage, 'goToCheckoutPage');
            setOPCReceiptPageChangeType('addCoverage');
        }
    };

    const minEffectiveDate = quoteEffectiveDate
        ? convertLocalDateDTOToDate(quoteEffectiveDate)
        : new Date();

    const shouldMoveInfoBoxToBottom =
        AddCoverageConfig.shouldMoveInfoBoxToBottom[productName]?.[coverageId];

    return (
        <ModalOverlay isOpen={isOpen} isFullScreen={isFullScreen}>
            <ModalDialog
                isFullScreen={isFullScreen}
                closeIconHandler={() => {
                    if (isOpcFetching) return;
                    handleCloseModal(closeCallback);
                }}
            >
                <ModalHeader />
                <ModalBody>
                    {isOpcFetching ? (
                        <LoadingDots />
                    ) : (
                        <>
                            <CoveragePriceOPC
                                hasChildren={isCoverageEffDateAdjustable || hasHighlightNote}
                                labels={coveragePriceOPClabels}
                                coveragePrice={coveragePrice}
                                oldTotalPrice={oldTotalPrice}
                                newTotalPrice={newTotalPrice}
                                currencyPostfix={currencyPostfix}
                                isChildrenPositionBeforePrice={!hasHighlightNote}
                                paymentFrequency={
                                    selectedPaymentFrequency ?? paymentFrequencyMap.annual
                                }
                            >
                                {isCoverageEffDateAdjustable ? (
                                    <>
                                        <h6>{labels.effectiveDateSubTitle}</h6>
                                        <DateField
                                            dateFormat="d. MMMM yyyy"
                                            minDate={minEffectiveDate}
                                            onChange={updateCoverageDate}
                                            selected={
                                                addCoverageDTO.effectiveDate
                                                    ? convertLocalDateDTOToDate(
                                                          addCoverageDTO.effectiveDate
                                                      )
                                                    : minEffectiveDate
                                            }
                                        />
                                    </>
                                ) : (
                                    <></>
                                )}
                                <HighlightInfoForCoverage
                                    coverageId={coverageId}
                                    shouldHide={shouldMoveInfoBoxToBottom}
                                    editCoverageType="add"
                                    coverageSubtitle={coverageSubtitle}
                                    flavourName={flavourName}
                                />
                            </CoveragePriceOPC>
                            {isRuleActive && !(isFromDAFlow && isAddedCoverable) && (
                                <h6>{labels.effectiveDateRule}</h6>
                            )}
                            <HighlightInfoForCoverage
                                coverageId={coverageId}
                                shouldHide={!shouldMoveInfoBoxToBottom}
                                editCoverageType="add"
                                flavourName={flavourName}
                            />
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        disabled={isOpcFetching || !isOpcDataFilled}
                        onClick={() => {
                            trackAddProduct(trackAddedCoverage);
                            if (businessFlow === 'OPC') {
                                setIsOpen(false);
                                setShowLoadingModal(true);
                            } else {
                                handleCloseModal(goToShoppingBasket, 'goToShoppingBasket');
                            }
                        }}
                    >
                        {labels.submitBtnText}
                    </Button>
                    <Button
                        disabled={isOpcFetching}
                        theme="secondary"
                        onClick={() => {
                            handleCloseModal(closeCallback);
                        }}
                    >
                        {labels.cancelBtnText}
                    </Button>
                </ModalFooter>
            </ModalDialog>
            {showLoadingModal && (
                <LoadingModal
                    loadingTitle={getHippoText('DSS-LOADING-HEADER')}
                    loadingText={getHippoText('DSS-LOADING-TEXT')}
                    showModal={showLoadingModal}
                    closeModal={() => {
                        setShowLoadingModal(false);
                    }}
                    backgroundTask={quoteJobInBackground}
                    onFinished={onFinished}
                />
            )}
            {stopCause !== 'None' && (
                <CustomerStopModal
                    showModal={true}
                    stopCause={stopCause}
                    closeModal={() => {
                        setIsOpen(true);
                        setStopCause('None');
                    }}
                />
            )}
        </ModalOverlay>
    );
};

export default AddCoverageModal;
